import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { purchasingApi } from '@api/purchasingApi';
import type {
  SupplierFilters,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  PurchaseOrderFilters,
  PurchaseOrderLineDto,
  PurchaseOrderLineInput,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
} from '@/types/purchasing';

// ── Suppliers ─────────────────────────────────────────────

export function useSuppliers(mdFilters: SupplierFilters) {
  return useQuery({
    queryKey: ['purchasing', 'suppliers', mdFilters],
    queryFn: () => purchasingApi.getSuppliers(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useSupplierById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['purchasing', 'suppliers', mdId],
    queryFn: () => purchasingApi.getSupplierById(mdId!),
    enabled: !!mdId,
  });
}

export function useSupplierSearch(mdQuery: string) {
  return useQuery({
    queryKey: ['purchasing', 'suppliers', 'search', mdQuery],
    queryFn: () => purchasingApi.searchSuppliers(mdQuery),
    enabled: mdQuery.length >= 2,
  });
}

export function useCreateSupplier() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateSupplierRequest) =>
      purchasingApi.createSupplier(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'suppliers'] });
      message.success('Постачальника створено');
    },
    onError: () => {
      message.error('Помилка при створенні постачальника');
    },
  });
}

export function useUpdateSupplier() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateSupplierRequest) =>
      purchasingApi.updateSupplier(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'suppliers'] });
      message.success('Постачальника оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні постачальника');
    },
  });
}

// ── Purchase Orders ───────────────────────────────────────

export function usePurchaseOrders(mdFilters: PurchaseOrderFilters) {
  return useQuery({
    queryKey: ['purchasing', 'orders', mdFilters],
    queryFn: () => purchasingApi.getPurchaseOrders(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function usePurchaseOrderById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['purchasing', 'orders', mdId],
    queryFn: () => purchasingApi.getPurchaseOrderById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreatePurchaseOrder() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreatePurchaseOrderRequest) =>
      purchasingApi.createPurchaseOrder(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
      message.success('Замовлення створено');
    },
    onError: () => {
      message.error('Помилка при створенні замовлення');
    },
  });
}

export function useUpdatePurchaseOrder() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    // Шапка — PUT, рядки — окремими запитами (UpdatePurchaseOrderCommand рядків не приймає)
    mutationFn: async (mdParams: {
      request: UpdatePurchaseOrderRequest;
      originalLines: PurchaseOrderLineDto[];
      lines: PurchaseOrderLineInput[];
    }) => {
      await purchasingApi.updatePurchaseOrder(mdParams.request);
      await purchasingApi.syncPurchaseOrderLines(
        mdParams.request.id,
        mdParams.originalLines,
        mdParams.lines
      );
    },
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
      message.success('Замовлення оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні замовлення');
    },
  });
}

export function useSubmitPO() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => purchasingApi.submitPurchaseOrder(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
      message.success('Замовлення подано на затвердження');
    },
    onError: () => {
      message.error('Помилка при поданні замовлення');
    },
  });
}

export function useApprovePO() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => purchasingApi.approvePurchaseOrder(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
      message.success('Замовлення затверджено');
    },
    onError: () => {
      message.error('Помилка при затвердженні замовлення');
    },
  });
}

export function useCancelPO() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { id: string; reason: string }) =>
      purchasingApi.cancelPurchaseOrder(mdParams.id, mdParams.reason),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
      message.success('Замовлення скасовано');
    },
    onError: () => {
      message.error('Помилка при скасуванні замовлення');
    },
  });
}

export function useAddPOLine() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: {
      orderId: string;
      line: { productId: string; quantity: number; unitPrice: number };
    }) => purchasingApi.addPurchaseOrderLine(mdParams.orderId, mdParams.line),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
      message.success('Рядок додано');
    },
    onError: () => {
      message.error('Помилка при додаванні рядка');
    },
  });
}

export function useRemovePOLine() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { orderId: string; lineId: string }) =>
      purchasingApi.removePurchaseOrderLine(mdParams.orderId, mdParams.lineId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
      message.success('Рядок видалено');
    },
    onError: () => {
      message.error('Помилка при видаленні рядка');
    },
  });
}
